// Ported from dev-scrips-pm-react src/patients/components/chart/labs-chart-tab.tsx (PROD-1119 Patients UI port).
/**
 * LabsChartTab — Labs inner tab of the patient Chart shell (PROD-738
 * PR A, read-only). Order / Cancel / Result entry ships in a follow-up.
 *
 * Flutter ref: `patient_chart/.../tab_widgets/labs_tab.dart`.
 *
 * The wire returns `PatientLab[]` where each lab record wraps a NESTED
 * `radiologyOrderRequest[]` array of order entries. A single PatientLab
 * can carry multiple test orders (e.g. "CBC + Lipid panel + Glucose"
 * ordered together). We flatten one row per (lab, order-entry) pair so
 * each ordered test is its own visible row rather than concatenating
 * displays in a single cell.
 *
 * **Naming caveat preserved** — the wire field is
 * `radiologyOrderRequest` but it covers BOTH laboratory and radiology
 * orders. The `categoryName` field (e.g. "Laboratory" / "Radiology")
 * is the actual discriminator the UI shows.
 *
 * FHIR: `ServiceRequest R4` (lab/diagnostic orders).
 *
 * PROD-2643: above the orders table, discrete results grouped by lab request
 * (`LabResultsSection`, FHIR DiagnosticReport + Observation). It renders only
 * once the results store answers (`storeAvailable: true`); until then the tab
 * is exactly the orders table it always was.
 */

import { StatusChip } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { usePatientLabs } from '@/patients/services/use-patient-labs';
import { ChartSectionHeader } from './chart-section-header';
import { useReportChartCount } from './chart-page-context';
import { ChartShowAll } from './chart-row-cap';
import { useRowCap } from './use-row-cap';
import { LabResultsSection } from './labs/lab-results-section';
import { useLabResultGroups } from './labs/use-lab-result-groups';
import type {
  LabOrderEntry,
  PatientLab,
} from '@/types/patient-chart-api';
import { V2_BODY_EMPHASIS, v2Look } from '@/shared/ui/design-v2';
import { CHART_TD_V2, CHART_TH_V2, chartCell, chartTableCard, type ChartCell } from './chart-table-v2';
import { EmptyState } from '@scripsteam/scrips-signal-ds';

interface FlatRow {
  /** Parent lab record id — for keying. */
  labId: string | null | undefined;
  /** This specific order entry. */
  entry: LabOrderEntry;
  /** Parent record's createdDate (when the order was placed). */
  createdDate: string | null | undefined;
  /** Parent record's performer (who ordered the tests). */
  performer: string | null | undefined;
  /** Parent record's plain-text note (applies to the whole order, not the
   *  individual test). Sourced from `notePlainText`. */
  parentNotes: string | null | undefined;
}

export interface LabsChartTabProps {
  patientId: string | null | undefined;
}

export function LabsChartTab({ patientId }: LabsChartTabProps) {
  const { data, isLoading, isError, refetch } = usePatientLabs(patientId);
  const results = useLabResultGroups(patientId);
  const showResults =
    !!patientId &&
    ((results.available && (results.groups.length > 0 || results.externalGroups.length > 0)) ||
      results.failed.length > 0);

  // Filter tombstoned lab records, then flatten one row per nested
  // order entry. Skip entries that are themselves tombstoned.
  const rows: FlatRow[] = [];
  for (const lab of (data ?? []).filter(
    (l): l is PatientLab => l.isDeleted !== true,
  )) {
    const entries = lab.radiologyOrderRequest ?? [];
    if (entries.length === 0) continue;
    for (const entry of entries) {
      if (entry.isDeleted === true) continue;
      rows.push({
        labId: lab.identifier,
        entry,
        createdDate: lab.createdDate,
        performer: lab.performerDetails?.name,
        parentNotes: lab.notePlainText,
      });
    }
  }

  // The index's count: the lab results (groups) when the results store answers, else the lab orders.
  useReportChartCount(isLoading ? null : showResults ? results.groups.length + results.externalGroups.length : rows.length);
  const cap = useRowCap(rows);

  if (!patientId) {
    return (
      <div className="p-6">
        <EmptyState
            icon="info"
            title="Save the patient first to view labs."
          />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <EmptyState
            icon="warning"
            title="Couldn't load labs."
            ctaLabel="Retry"
            onCtaClick={() => void refetch()}
          />
      </div>
    );
  }

  if (rows.length === 0 && showResults) {
    return (
      <div className="p-6 space-y-3">
        <LabResultsSection
          patientId={patientId}
          groups={results.groups}
          externalGroups={results.externalGroups}
          labsByRoot={results.labsByRoot}
          failed={results.failed}
        />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
            icon="labs"
            title="No lab orders for this patient."
            description="Lab + radiology orders appear here once the encounter creates them."
          />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-3">
      {showResults ? (
        <LabResultsSection
          patientId={patientId}
          groups={results.groups}
          externalGroups={results.externalGroups}
          labsByRoot={results.labsByRoot}
          failed={results.failed}
        />
      ) : null}
      {/* Labs are ordered from the encounter screen; chart view is read-only. */}
      <ChartSectionHeader title="Labs & radiology" count={rows.length} showAdd={false} />
      <div className={chartTableCard('rounded-lg border border-border-default overflow-hidden bg-surface-card')}>
        <table className="w-full text-sm">
          <thead className="bg-grey-100">
            <tr className="text-left text-text-muted">
              <Th>#</Th>
              <Th>Test</Th>
              <Th>Category</Th>
              <Th>Status</Th>
              <Th>Priority</Th>
              <Th>Ordered</Th>
              <Th>Ordered by</Th>
              <Th>Note</Th>
            </tr>
          </thead>
          <tbody>
            {cap.visible.map((row, i) => (
              <tr
                key={`${row.labId ?? i}-${row.entry.id ?? row.entry.code?.code ?? i}`}
                className="border-t border-border-default hover:bg-grey-100/50"
              >
                <Td cell="index">{i + 1}</Td>
                <Td cell="name">
                  <span className={v2Look('font-medium text-brand-depth', V2_BODY_EMPHASIS)}>
                    {row.entry.code?.displayName ?? '—'}
                  </span>
                  {row.entry.code?.code ? (
                    <div className="text-xs text-text-hint">
                      {row.entry.code.code}
                    </div>
                  ) : null}
                </Td>
                <Td cell="short">{categoryBadge(row.entry.categoryName)}</Td>
                <Td cell="short">{statusBadge(row.entry.status)}</Td>
                <Td cell="short">{priorityBadge(row.entry.priority)}</Td>
                <Td date>{formatDate(row.createdDate)}</Td>
                <Td cell="text">{row.performer ?? '—'}</Td>
                <Td cell="note" tip={row.entry.notePlainText?.trim() ||
                      row.parentNotes?.trim() ||
                      '—'}>{row.entry.notePlainText?.trim() ||
                      row.parentNotes?.trim() ||
                      '—'}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ChartShowAll cap={cap} label="lab orders" />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className={chartCell('px-3 py-2 text-xs font-bold uppercase tracking-wide text-left', CHART_TH_V2)}>
      {children}
    </th>
  );
}

function Td({
  children,
  date = false,
  cell,
  tip,
}: {
  children: React.ReactNode;
  date?: boolean;
  /** What the column holds (the bridge sizes it: index, name, text, short, date, note). */
  cell?: ChartCell;
  /** A note's full text for the app's instant tooltip, shown when the one-line cell is cut off. */
  tip?: string;
}) {
  return (
    <td data-chart-cell={date ? 'date' : cell} data-tip={tip} className={chartCell('px-3 py-2 text-left align-top', CHART_TD_V2)}>
      {children}
    </td>
  );
}

function categoryBadge(raw: string | null | undefined) {
  if (!raw) return <span className="text-text-hint">—</span>;
  // A category is a kind, not a status: plain text (it was a blue "laboratory" pill and a yellow radiology one, a
  // status colour for a category; the soft chip is for statuses). Design rules, "Status" and "Colour".
  return <span>{raw}</span>;
}

/**
 * PROD-2660: a cancelled order reads as void, not as an error: the muted grey
 * chip with the label struck through, the same look as the pm-react lab desk
 * (#1099). Covers the Scrips spelling Patient writes ("Cancelled", PROD-2629)
 * and the FHIR ServiceRequest codes for the same thing ("cancelled"/"revoked",
 * which Billing also reads as Cancelled). "entered-in-error" stays red.
 */
function isCancelledOrderStatus(raw: string | null | undefined): boolean {
  const lower = (raw ?? '').toLowerCase().trim();
  return lower === 'cancelled' || lower === 'canceled' || lower === 'revoked';
}

function statusBadge(raw: string | null | undefined) {
  if (!raw) return <span className="text-text-hint">—</span>;
  const lower = raw.toLowerCase().trim();
  if (lower === 'n/a' || lower === '')
    return <span className="text-text-hint">—</span>;
  if (isCancelledOrderStatus(raw)) {
    return (
      <StatusChip
        tone="soft"
        semantic="neutral"
        className="line-through"
        data-testid="lab-status-cancelled"
      >
        {raw}
      </StatusChip>
    );
  }
  const semantic =
    lower.includes('completed') || lower.includes('reported')
      ? 'success'
      : lower.includes('active') || lower.includes('in-progress')
        ? 'info'
        : lower.includes('entered-in-error')
          ? 'error'
          : lower.includes('draft') || lower.includes('on-hold')
            ? 'warning'
            : 'neutral';
  return (
    <StatusChip tone="soft" semantic={semantic} data-chip-look={lower.includes('draft') ? 'draft' : undefined}>
      {raw}
    </StatusChip>
  );
}

function priorityBadge(raw: string | null | undefined) {
  if (!raw) return <span className="text-text-hint">—</span>;
  const lower = raw.toLowerCase().trim();
  if (lower === 'n/a' || lower === '')
    return <span className="text-text-hint">—</span>;
  // The one soft status chip (was a 12 px tinted pill): red only for an order that asks for action now (stat, urgent,
  // asap), amber for high, grey for the rest.
  const semantic =
    lower.includes('stat') || lower.includes('urgent') || lower.includes('asap')
      ? 'error'
      : lower.includes('high')
        ? 'warning'
        : 'neutral';
  return (
    <StatusChip tone="soft" semantic={semantic}>
      {raw}
    </StatusChip>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso || iso === 'n/a') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}
